import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * Design system V2 buttons (40 px, radius 10, 14/20 600).
 * One primary (teal fill) per screen or panel.
 */
export const buttonVariants = cva('clinio-btn', {
  variants: {
    variant: {
      primary: 'clinio-btn--primary',
      secondary: 'clinio-btn--secondary',
      tertiary: 'clinio-btn--tertiary',
      ghost: 'clinio-btn--ghost',
      danger: 'clinio-btn--danger',
      link: 'clinio-btn--link',
    },
    size: {
      sm: 'clinio-btn--sm',
      md: '',
      lg: '',
      full: 'clinio-btn--full',
      icon: 'clinio-btn--icon',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingText?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', loading = false, loadingText, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="animate-spin" />
          {loadingText ?? children}
        </>
      ) : (
        children
      )}
    </button>
  );
});

export type ButtonLinkProps = Omit<LinkProps, 'className'> &
  VariantProps<typeof buttonVariants> & {
    className?: string;
    children?: ReactNode;
  };

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { className, variant, size, ...props },
  ref,
) {
  return <Link ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});

export type ButtonAnchorProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> &
  VariantProps<typeof buttonVariants> & {
    className?: string;
  };

export const ButtonAnchor = forwardRef<HTMLAnchorElement, ButtonAnchorProps>(function ButtonAnchor(
  { className, variant, size, ...props },
  ref,
) {
  return <a ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});

export function ButtonGroup({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children?: ReactNode }) {
  return <div className={cn('clinio-btn-group', className)} {...props} />;
}
